import { Suspense, type ComponentType, type ReactElement, lazy } from 'react';
import { Card } from './Card';
import { secondaryTextClassName } from './styles';

/**
 * Découpage de code par route (audit Impeccable `/impeccable optimize`,
 * 2026-09-07).
 *
 * PROBLÈME RÉEL mesuré avant ce changement : `vite build` produisait UN SEUL
 * chunk JS de 606,66 kB (157,19 kB gzip) pour web-admin et 457,23 kB
 * (132,39 kB gzip) pour web-parent — `App.tsx` importait statiquement ses
 * ~40 pages, donc ouvrir `/login` téléchargeait aussi le dashboard financier,
 * le journal d'audit, l'assistant d'onboarding et l'écran MFA. Vite émettait
 * d'ailleurs son propre avertissement ("Some chunks are larger than 500 kB").
 *
 * Ce module est PARTAGÉ par les deux apps (`apps/web-admin`/`apps/web-parent`
 * l'importent via `@monecolier/ui`) plutôt que dupliqué : c'est exactement le
 * même besoin des deux côtés, et une divergence de fallback ferait clignoter
 * les deux portails différemment sans raison métier — même logique que le
 * thème partagé (voir theme.css) et que `styles.ts`.
 */

/**
 * Repli affiché pendant le téléchargement du chunk d'une route.
 *
 * Rendu DANS une `Card`, et non en texte nu : sans elle, la page perdrait son
 * panneau blanc le temps du chargement, l'arrière-plan `bg-surface-muted` de `body`
 * apparaîtrait en plein, puis la carte surgirait — un saut de mise en page
 * visible sur une connexion lente, précisément le cas que ce découpage vise à
 * améliorer. La `Card` réserve la place.
 *
 * `role="status"` (et non `aria-live="assertive"` ni un simple `<p>`) : même
 * convention que les états de chargement déjà en place dans les pages
 * (`Students.tsx`, `AuthProvider`), annoncé poliment par un lecteur d'écran
 * sans interrompre la lecture en cours.
 *
 * Texte identique à celui déjà utilisé partout dans le produit ("Chargement…",
 * avec de vraies points de suspension typographiques) : un utilisateur ne doit
 * pas pouvoir deviner, au libellé, qu'un mécanisme technique différent est à
 * l'œuvre selon l'écran.
 */
export const routeLoadingFallback: ReactElement = (
  <Card size="page">
    <p className={secondaryTextClassName} role="status">
      Chargement…
    </p>
  </Card>
);

/**
 * Enveloppe une route paresseuse dans sa `<Suspense>`.
 *
 * Une `<Suspense>` PAR ROUTE plutôt qu'une seule autour de `<Routes>` : avec
 * une frontière unique, React démonterait l'arbre entier à chaque navigation
 * vers une route pas encore téléchargée, y compris les parties déjà rendues.
 * Une frontière par route limite le repli au contenu de la page, la Sidebar
 * (montée une seule fois hors `<Routes>`, voir App.tsx) restant intacte et
 * navigable pendant le téléchargement.
 */
export function lazyRoute(element: ReactElement): ReactElement {
  return <Suspense fallback={routeLoadingFallback}>{element}</Suspense>;
}

/**
 * Composant de page tel que ce dépôt les écrit : aucune prop, tout état venant
 * des hooks de route (`useParams`, `useSearchParams`) ou du contexte d'auth.
 * Vérifié sur les 42 pages des deux apps avant d'être posé comme contrat ;
 * une page qui aurait besoin d'une prop passerait par la route, pas par ici.
 */
type PageComponent = ComponentType;

/**
 * `React.lazy` n'accepte qu'un module à export DÉFAUT, or toutes les pages de
 * ce dépôt sont des exports NOMMÉS (`export function Students()`) — convention
 * suivie partout et volontairement conservée : basculer ~40 fichiers en export
 * défaut serait un changement bien plus large que ce correctif, et casserait
 * les imports de leurs tests.
 *
 * Cet adaptateur fait la conversion à un seul endroit :
 *
 *   const Students = lazyNamed(() => import('./pages/Students'), 'Students');
 *
 * Le paramètre générique `K` lie le nom demandé aux clés réellement exportées
 * par le module : se tromper de nom, ou renommer la page sans mettre à jour
 * l'appel, échoue à la compilation plutôt qu'à l'exécution — un `undefined`
 * rendu comme composant ne produirait qu'une page blanche et une erreur
 * console au moment de la navigation.
 */
export function lazyNamed<K extends string>(
  factory: () => Promise<Record<K, PageComponent>>,
  name: K,
): PageComponent {
  return lazy(async () => {
    // Annotation explicite : tant que `K` reste générique, TypeScript diffère
    // la résolution de `Record<K, PageComponent>[K]` et n'admet pas de lui-même
    // qu'elle vaut `PageComponent`, ce qui fait échouer l'inférence du type de
    // retour de `lazy`. Nommer la variable force la simplification ici, sans
    // aucun `as` (une assertion masquerait un vrai décalage de type au lieu de
    // le signaler).
    const page: PageComponent = (await factory())[name];
    return { default: page };
  });
}
